<template>
    <div class="menu-hint"
        :id="id"
        role="tooltip"
        :class="{
            'menu-hint-visible': isVisible,
            'menu-hint-gliding': isGliding,
        }"
        :style="{
            top: top + 'px',
            left: left + 'px',
            '--item-color': item ? item.color : null,
        }"
        @mouseenter="_cancelClose()"
        @mouseleave="requestClose()">
        <span class="menu-hint-arrow" :style="{top: arrowTop + 'px'}"></span>

        <div class="menu-hint-content" v-if="item" :key="item.description">
            <span class="menu-hint-icon">
                <fa :icon="item.icon" />
            </span>

            <div class="menu-hint-text">
                <p class="menu-hint-title">
                    {{item.description}}
                    <span class="menu-hint-current" v-if="item.active">você está aqui</span>
                </p>
                <p class="menu-hint-description">{{item.hint}}</p>
            </div>
        </div>
    </div>
</template>

<script>
const VIEWPORT_MARGIN = 8
const ARROW_EDGE = 14
const CLOSE_DELAY = 100
const SKIP_DELAY_WINDOW = 300

export default {
    name: 'MenuHintCmp',
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            item: null,
            isVisible: false,
            isGliding: false,
            top: 0,
            left: 0,
            arrowTop: 0,
            lastClosedAt: 0,
        }
    },
    beforeDestroy() {
        this._clearTimers()
        document.removeEventListener('keydown', this._onKeydown)
    },
    methods: {
        requestOpen(anchor, item, delay) {
            this._cancelClose()
            clearTimeout(this.openTimer)

            const recentlyClosed = Date.now() - this.lastClosedAt < SKIP_DELAY_WINDOW

            if (this.isVisible || recentlyClosed) {
                this.open(anchor, item)
                return
            }

            this.openTimer = setTimeout(() => this.open(anchor, item), delay)
        },
        open(anchor, item) {
            this._cancelClose()
            clearTimeout(this.openTimer)

            this.isGliding = this.isVisible
            this.item = item

            this.$nextTick(() => {
                this._position(anchor)

                if (!this.isVisible) {
                    this.isVisible = true
                    this.$emit('change', item)
                    document.addEventListener('keydown', this._onKeydown)
                } else {
                    this.$emit('change', item)
                }
            })
        },
        requestClose() {
            clearTimeout(this.openTimer)
            this._cancelClose()
            this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY)
        },
        close() {
            this._clearTimers()

            if (!this.isVisible) {
                return
            }

            this.isVisible = false
            this.isGliding = false
            this.lastClosedAt = Date.now()
            document.removeEventListener('keydown', this._onKeydown)
            this.$emit('change', null)
        },
        _position(anchor) {
            const anchorRect = anchor.getBoundingClientRect()
            const asideRect = anchor.closest('aside').getBoundingClientRect()
            const height = this.$el.offsetHeight
            const center = anchorRect.top + anchorRect.height / 2
            const maxTop = window.innerHeight - height - VIEWPORT_MARGIN
            const top = Math.max(VIEWPORT_MARGIN, Math.min(center - height / 2, maxTop))

            this.top = top
            this.left = asideRect.right + 10
            this.arrowTop = Math.max(ARROW_EDGE, Math.min(center - top, height - ARROW_EDGE))
        },
        _onKeydown(ev) {
            if (ev.key === 'Escape') {
                this.close()
            }
        },
        _cancelClose() {
            clearTimeout(this.closeTimer)
        },
        _clearTimers() {
            clearTimeout(this.openTimer)
            clearTimeout(this.closeTimer)
        },
    },
}
</script>

<style lang="scss" scoped>
.menu-hint {
    position: fixed;
    z-index: 1050;
    width: 248px;
    padding: 12px;
    background-color: var(--surface-hint);
    border: 1px solid color-mix(in srgb, var(--white) 8%, transparent);
    border-radius: 12px;
    box-shadow: 0 8px 24px color-mix(in srgb, var(--shadow-ink) 18%, transparent), 0 2px 6px color-mix(in srgb, var(--shadow-ink) 12%, transparent);
    color: var(--text-on-brand);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateX(-4px);
    transition: opacity .1s ease, transform .1s ease, visibility 0s linear .1s;

    &.menu-hint-visible {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transform: translateX(0);
        transition: opacity .15s ease, transform .15s cubic-bezier(.2, .8, .2, 1), visibility 0s;
    }

    &.menu-hint-gliding {
        transition: opacity .15s ease, transform .15s ease, top .16s cubic-bezier(.4, 0, .2, 1), visibility 0s;

        .menu-hint-arrow {
            transition: top .16s cubic-bezier(.4, 0, .2, 1);
        }
    }

    .menu-hint-arrow {
        position: absolute;
        left: -6px;
        width: 10px;
        height: 10px;
        margin-top: -5px;
        background-color: var(--surface-hint);
        border-left: 1px solid color-mix(in srgb, var(--white) 8%, transparent);
        border-bottom: 1px solid color-mix(in srgb, var(--white) 8%, transparent);
        transform: rotate(45deg);
    }

    .menu-hint-content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        animation: menu-hint-content-in .16s ease;
    }

    .menu-hint-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background-color: var(--item-color, var(--brand-fill));
        color: var(--text-on-accent);
        font-size: .95em;
        box-shadow: inset 0 1px 0 color-mix(in srgb, var(--white) 18%, transparent);
    }

    .menu-hint-text {
        min-width: 0;

        p {
            margin-bottom: 0;
        }
    }

    .menu-hint-title {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 2px 8px;
        font-weight: 600;
        font-size: .95em;
        line-height: 1.3;
    }

    .menu-hint-current {
        font-weight: 400;
        font-size: .75em;
        color: color-mix(in srgb, var(--white) 55%, transparent);
    }

    .menu-hint-description {
        margin-top: 2px;
        font-size: .85em;
        line-height: 1.4;
        color: color-mix(in srgb, var(--white) 72%, transparent);
    }
}

@keyframes menu-hint-content-in {
    from {
        opacity: 0;
        transform: translateY(2px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .menu-hint,
    .menu-hint.menu-hint-visible,
    .menu-hint.menu-hint-gliding {
        transform: none;
        transition: opacity .12s ease, visibility 0s;
    }

    .menu-hint .menu-hint-content {
        animation: none;
    }

    .menu-hint.menu-hint-gliding .menu-hint-arrow {
        transition: none;
    }
}
</style>
